import { TimePeriod } from './config/user-enums'

function formatDate(date: Date): string {
  return date.toISOString().split('T')[0]
}

export function getDateRange(currentFilter: string) {
  let startDate: string
  let endDate: string = formatDate(new Date())

  switch (currentFilter) {
    case TimePeriod.DAILY: {
      const today = new Date()
      startDate = formatDate(today)
      endDate = formatDate(today)
      break
    }
    case TimePeriod.WEEKLY: {
      const startOfWeek = new Date()
      startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay())
      const endOfWeek = new Date(startOfWeek)
      endOfWeek.setDate(endOfWeek.getDate() + 6)
      startDate = formatDate(startOfWeek)
      endDate = formatDate(endOfWeek)
      break
    }
    case TimePeriod.MONTHLY: {
      const startOfMonth = new Date()
      startOfMonth.setDate(1)
      const endOfMonth = new Date(startOfMonth)
      endOfMonth.setMonth(endOfMonth.getMonth() + 1)
      endOfMonth.setDate(0)
      startDate = formatDate(startOfMonth)
      endDate = formatDate(endOfMonth)
      break
    }
    case TimePeriod.YEARLY: {
      const startOfYear = new Date()
      startOfYear.setMonth(0, 1)
      const endOfYear = new Date(startOfYear)
      endOfYear.setFullYear(endOfYear.getFullYear() + 1)
      endOfYear.setDate(0)
      startDate = formatDate(startOfYear)
      endDate = formatDate(endOfYear)
      break
    }
    case TimePeriod.ALL: {
      startDate = '2021-01-01'
      endDate = formatDate(new Date())
      break
    }
    default:
      throw new Error('Invalid filter')
  }

  return { startDate, endDate }
}

// Check if the code is running in a browser environment
export const isBrowser = typeof window !== 'undefined'

export const formatPhoneNumber = (
  phoneNumber: number | undefined | string,
  countryCode: string | undefined,
) => {
  const defaultPhoneNumber = phoneNumber ?? 'N/A'
  const defaultCountryCode = countryCode ?? ''

  if (defaultPhoneNumber === 'N/A') {
    return '--'
  }

  const formattedDigits = defaultPhoneNumber.toString().replace(/\D/g, '')

  if (formattedDigits.length <= 3) {
    return `${defaultCountryCode} ${formattedDigits}`
  } else if (formattedDigits.length <= 6) {
    return `${defaultCountryCode} ${formattedDigits.slice(0, 3)}-${formattedDigits.slice(3)}`
  } else {
    return `${defaultCountryCode} ${formattedDigits.slice(0, 3)}-${formattedDigits.slice(3, 6)}-${formattedDigits.slice(6, 10)}`
  }
}

export const unformattedPhoneNumber = (
  formattedPhone: string,
  countryCode: string,
): string => {
  const cleanedNumber = formattedPhone.replace(/\D/g, '')

  const countryCodeDigits = countryCode.replace(/\D/g, '')

  let numberWithoutCountryCode = cleanedNumber.startsWith(countryCodeDigits)
    ? cleanedNumber.slice(countryCodeDigits.length)
    : cleanedNumber

  if (numberWithoutCountryCode.length > 10) {
    numberWithoutCountryCode = numberWithoutCountryCode.slice(0, 10)
  }

  return numberWithoutCountryCode
}

export const validatePhoneNumber = (
  phoneNumber: string,
): { isValid: boolean; errorMessage: string } => {
  const phonePattern = /^[2-9]\d{2}[2-9]\d{6}$/
  if (phoneNumber === '') {
    return {
      isValid: false,
      errorMessage: 'Please enter your phoneNumber number.',
    }
  }
  if (phoneNumber.length !== 10) {
    return {
      isValid: false,
      errorMessage: 'Phone number must contain exactly 10 digits.',
    }
  }
  if (!phonePattern.test(phoneNumber)) {
    return {
      isValid: false,
      errorMessage:
        'Phone number must be in the format NXX-NXX-XXXX, where N is between 2-9 and X is between 0-9.',
    }
  }
  return { isValid: true, errorMessage: '' }
}
export const formatTime = (dateString: string): string => {
  const date = new Date(dateString)
  return date.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  })
}

export const getDateTitle = (dateString: string): string => {
  const date = new Date(dateString)
  const today = new Date()
  const yesterday = new Date(today)
  yesterday.setDate(today.getDate() - 1)

  if (date.toDateString() === today.toDateString()) {
    return 'Today'
  } else if (date.toDateString() === yesterday.toDateString()) {
    return 'Yesterday'
  } else {
    return date.toLocaleDateString()
  }
}

export const stringAvatar = (name: string) => {
  return {
    children: `${name.split(' ')[0][0]?.toUpperCase()}${name.split(' ')[1][0]?.toUpperCase()}`,
  }
}

export const formatNumbers = (amount: number) => {
  return `${amount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`
}

export const formatDuration = (seconds: number) => {
  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = seconds % 60
  return `${String(minutes).padStart(2, '0')}:${String(remainingSeconds).padStart(2, '0')}`
}

export function debounce(fn: (value: string) => void, delay: number) {
  let timer: ReturnType<typeof setTimeout>
  return (value: string) => {
    clearTimeout(timer)
    timer = setTimeout(() => {
      fn(value)
    }, delay)
  }
}
